// src/client/components/settings/SettingsValueInput.tsx
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Input } from '@client/components/ui/input';
import { Textarea } from '@client/components/ui/textarea';
import { Switch } from '@client/components/ui/switch';
import { Edit3, Eye, EyeOff, X, Check } from 'lucide-react';
import type { Setting } from '@client/types/settings';

interface SettingsValueInputProps {
  setting: Setting;
  editingSetting: Setting | null;
  setEditingSetting: (setting: Setting | null) => void;
  jsonVisible: { [key: string]: boolean };
  setJsonVisible: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  saveSetting: (key: string, value: string, type?: string) => Promise<void>;
}

export default function SettingsValueInput({
  setting,
  editingSetting,
  setEditingSetting,
  jsonVisible,
  setJsonVisible,
  saveSetting,
}: SettingsValueInputProps) {
  const type = setting.type;
  const isEditing = editingSetting?.id === setting.id;

  // ============================================================================
  // VIEW MODE
  // ============================================================================
  
  if (!isEditing) {
    // JSON - Special handling with expandable formatted display
    if (type === 'json') {
      const isVisible = jsonVisible[setting.key];
      const formatted = isVisible 
        ? JSON.stringify(JSON.parse(setting.value), null, 2)
        : null;
      
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">JSON</Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setJsonVisible((prev) => ({
                  ...prev,
                  [setting.key]: !prev[setting.key],
                }))
              }
            >
              {isVisible ? (
                <>
                  <EyeOff className="h-3 w-3 mr-1" />
                  <span className="text-xs">Ausblenden</span>
                </>
              ) : (
                <>
                  <Eye className="h-3 w-3 mr-1" />
                  <span className="text-xs">Anzeigen</span>
                </>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditingSetting(setting)}>
              <Edit3 className="h-4 w-4" />
            </Button>
          </div>
          
          {isVisible && (
            <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto border">
              <code>{formatted}</code>
            </pre>
          )}
        </div>
      );
    }

    // Boolean - Badge display
    if (type === 'boolean') {
      return (
        <div className="flex items-center gap-2">
          <Badge variant={setting.value === 'true' ? 'default' : 'secondary'}>
            {setting.value === 'true' ? 'Enabled' : 'Disabled'}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setEditingSetting(setting)}>
            <Edit3 className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    // Number - Monospace display
    if (type === 'number') {
      return (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm">{setting.value}</span>
          <Button variant="ghost" size="sm" onClick={() => setEditingSetting(setting)}>
            <Edit3 className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    // String (default)
    return (
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm">{setting.value}</span>
        <Button variant="ghost" size="sm" onClick={() => setEditingSetting(setting)}>
          <Edit3 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // ============================================================================
  // EDIT MODE
  // ============================================================================

  // Edit Mode - Boolean
  if (type === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <Switch
          checked={setting.value === 'true'}
          onCheckedChange={(checked) => {
            const newValue = checked ? 'true' : 'false';
            saveSetting(setting.key, newValue, 'boolean');
          }}
        />
        <span className="text-sm text-muted-foreground">
          {setting.value === 'true' ? 'Enabled' : 'Disabled'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => setEditingSetting(null)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Edit Mode - Number
  if (type === 'number') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          defaultValue={setting.value}
          onBlur={(e) => {
            saveSetting(setting.key, e.target.value, 'number');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              saveSetting(setting.key, e.currentTarget.value);
            } else if (e.key === 'Escape') {
              setEditingSetting(null);
            }
          }}
          className="flex-1"
          autoFocus
        />
        <Button variant="ghost" size="sm" onClick={() => setEditingSetting(null)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Edit Mode - JSON
  if (type === 'json') {
    return (
      <div className="space-y-2">
        <Textarea
          defaultValue={setting.value}
          onBlur={(e) => {
            try {
              JSON.parse(e.target.value);
              saveSetting(setting.key, e.target.value, 'json');
            } catch {
              // Invalid JSON, don't save
            }
          }}
          className="min-h-[100px] font-mono text-sm"
          autoFocus
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
              if (textarea) {
                try {
                  JSON.parse(textarea.value);
                  saveSetting(setting.key, textarea.value);
                } catch {
                  // Show error - could add toast here
                }
              }
            }}
          >
            <Check className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditingSetting(null)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Edit Mode - String (default)
  return (
    <div className="flex items-center gap-2">
      <Input
        type="text"
        defaultValue={setting.value}
        onBlur={(e) => {
          saveSetting(setting.key, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            saveSetting(setting.key, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            setEditingSetting(null);
          }
        }}
        className="flex-1"
        autoFocus
      />
      <Button variant="ghost" size="sm" onClick={() => setEditingSetting(null)}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}